/*!
 * devextreme-angular
 * Version: 20.2.13
 * Build date: Fri Apr 07 2023
 *
 * Copyright (c) 2012 - 2023 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */


import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';
import { dxSchedulerScrolling } from 'devextreme/ui/scheduler';

import DxScheduler from 'devextreme/ui/scheduler';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper
} from 'devextreme-angular/core';

import { DxoAppointmentDraggingModule } from 'devextreme-angular/ui/nested';
import { DxoEditingModule } from 'devextreme-angular/ui/nested';
import { DxiResourceModule } from 'devextreme-angular/ui/nested';
import { DxoScrollingModule } from 'devextreme-angular/ui/nested';
import { DxiViewModule } from 'devextreme-angular/ui/nested';

import { DxiResourceComponent } from 'devextreme-angular/ui/nested';
import { DxiViewComponent } from 'devextreme-angular/ui/nested';



/**
 * The Scheduler is a UI component that represents scheduled data and allows a user to manage and edit it.

 */
@Component({
    selector: 'dx-scheduler',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxSchedulerComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxScheduler;

    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether the UI component adapts to small screens.
    
     */
    @Input()
    get adaptivityEnabled(): boolean {
        return this._getOption('adaptivityEnabled');
    }
    set adaptivityEnabled(value: boolean) {
        this._setOption('adaptivityEnabled', value);
    }


    /**
     * Specifies the name of the data source item field whose value defines whether or not the corresponding appointment is an all-day appointment.
    
     */
    @Input()
    get allDayExpr(): string {
        return this._getOption('allDayExpr');
    }
    set allDayExpr(value: string) {
        this._setOption('allDayExpr', value);
    }


    /**
     * Specifies a custom template for cell overflow indicators.
    
     */
    @Input()
    get appointmentCollectorTemplate(): any {
        return this._getOption('appointmentCollectorTemplate');
    }
    set appointmentCollectorTemplate(value: any) {
        this._setOption('appointmentCollectorTemplate', value);
    }


    /**
     * Configures appointment reordering using drag and drop gestures.
    
     */
    @Input()
    get appointmentDragging(): { autoScroll?: boolean, data?: any, group?: string, onAdd?: Function, onDragEnd?: Function, onDragMove?: Function, onDragStart?: Function, onRemove?: Function, scrollSensitivity?: number, scrollSpeed?: number } {
        return this._getOption('appointmentDragging');
    }
    set appointmentDragging(value: { autoScroll?: boolean, data?: any, group?: string, onAdd?: Function, onDragEnd?: Function, onDragMove?: Function, onDragStart?: Function, onRemove?: Function, scrollSensitivity?: number, scrollSpeed?: number }) {
        this._setOption('appointmentDragging', value);
    }


    /**
     * Specifies a custom template for appointments.
    
     */
    @Input()
    get appointmentTemplate(): any {
        return this._getOption('appointmentTemplate');
    }
    set appointmentTemplate(value: any) {
        this._setOption('appointmentTemplate', value);
    }


    /**
     * Specifies a custom template for tooltips displayed when users click an appointment or cell overflow indicator.
    
     */
    @Input()
    get appointmentTooltipTemplate(): any {
        return this._getOption('appointmentTooltipTemplate');
    }
    set appointmentTooltipTemplate(value: any) {
        this._setOption('appointmentTooltipTemplate', value);
    }


    /**
     * Specifies cell duration in minutes. This property's value should divide the interval between startDayHour and endDayHour into even parts.
    
     */
    @Input()
    get cellDuration(): number {
        return this._getOption('cellDuration');
    }
    set cellDuration(value: number) {
        this._setOption('cellDuration', value);
    }


    /**
     * Specifies whether or not an end-user can scroll the view in both directions at the same time.
    
     */
    @Input()
    get crossScrollingEnabled(): boolean {
        return this._getOption('crossScrollingEnabled');
    }
    set crossScrollingEnabled(value: boolean) {
        this._setOption('crossScrollingEnabled', value);
    }


    /**
     * Specifies the current date.
    
     */
    @Input()
    get currentDate(): Date | number | string {
        return this._getOption('currentDate');
    }
    set currentDate(value: Date | number | string) {
        this._setOption('currentDate', value);
    }


    /**
     * Specifies the displayed view. Accepts name or type of a view available in the views array.
    
     */
    @Input()
    get currentView(): string {
        return this._getOption('currentView');
    }
    set currentView(value: string) {
        this._setOption('currentView', value);
    }


    /**
     * Customizes the date navigator's text.
    
     */
    @Input()
    get customizeDateNavigatorText(): Function {
        return this._getOption('customizeDateNavigatorText');
    }
    set customizeDateNavigatorText(value: Function) {
        this._setOption('customizeDateNavigatorText', value);
    }


    /**
     * Specifies a custom template for table cells.
    
     */
    @Input()
    get dataCellTemplate(): any {
        return this._getOption('dataCellTemplate');
    }
    set dataCellTemplate(value: any) {
        this._setOption('dataCellTemplate', value);
    }


    /**
     * Binds the UI component to data.
    
     */
    @Input()
    get dataSource(): DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<DevExpress.ui.dxSchedulerAppointment> {
        return this._getOption('dataSource');
    }
    set dataSource(value: DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<DevExpress.ui.dxSchedulerAppointment>) {
        this._setOption('dataSource', value);
    }


    /**
     * Specifies a custom template for day scale items.
    
     */
    @Input()
    get dateCellTemplate(): any {
        return this._getOption('dateCellTemplate');
    }
    set dateCellTemplate(value: any) {
        this._setOption('dateCellTemplate', value);
    }


    /**
     * 
    
     */
    @Input()
    get dateSerializationFormat(): string {
        return this._getOption('dateSerializationFormat');
    }
    set dateSerializationFormat(value: string) {
        this._setOption('dateSerializationFormat', value);
    }


    /**
     * Specifies the name of the data source item field whose value holds the description of the corresponding appointment.
    
     */
    @Input()
    get descriptionExpr(): string {
        return this._getOption('descriptionExpr');
    }
    set descriptionExpr(value: string) {
        this._setOption('descriptionExpr', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies a custom template for tooltips displayed when users click a cell overflow indicator.
    
     * @deprecated Use the appointmentTooltipTemplate option instead.
    
     */
    @Input()
    get dropDownAppointmentTemplate(): any {
        return this._getOption('dropDownAppointmentTemplate');
    }
    set dropDownAppointmentTemplate(value: any) {
        this._setOption('dropDownAppointmentTemplate', value);
    }


    /**
     * Specifies which editing operations a user can perform on appointments.
    
     */
    @Input()
    get editing(): boolean | { allowAdding?: boolean, allowDeleting?: boolean, allowDragging?: boolean, allowEditingTimeZones?: boolean, allowResizing?: boolean, allowTimeZoneEditing?: boolean, allowUpdating?: boolean } {
        return this._getOption('editing');
    }
    set editing(value: boolean | { allowAdding?: boolean, allowDeleting?: boolean, allowDragging?: boolean, allowEditingTimeZones?: boolean, allowResizing?: boolean, allowTimeZoneEditing?: boolean, allowUpdating?: boolean }) {
        this._setOption('editing', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component's container element.
    
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the name of the data source item field that defines the ending of an appointment.
    
     */
    @Input()
    get endDateExpr(): string {
        return this._getOption('endDateExpr');
    }
    set endDateExpr(value: string) {
        this._setOption('endDateExpr', value);
    }


    /**
     * Specifies the name of the data source item field that defines the timezone of the appointment end date.
    
     */
    @Input()
    get endDateTimeZoneExpr(): string {
        return this._getOption('endDateTimeZoneExpr');
    }
    set endDateTimeZoneExpr(value: string) {
        this._setOption('endDateTimeZoneExpr', value);
    }


    /**
     * Specifies the last hour on the time scale. Accepts integer values from 0 to 24.
    
     */
    @Input()
    get endDayHour(): number {
        return this._getOption('endDayHour');
    }
    set endDayHour(value: number) {
        this._setOption('endDayHour', value);
    }


    /**
     * Specifies the first day of a week. Does not apply to the agenda view.
    
     */
    @Input()
    get firstDayOfWeek(): number | string {
        return this._getOption('firstDayOfWeek');
    }
    set firstDayOfWeek(value: number | string) {
        this._setOption('firstDayOfWeek', value);
    }


    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * If true, appointments are grouped by date first and then by resource; opposite if false. Applies only if appointments are grouped and groupOrientation is 'horizontal'.
    
     */
    @Input()
    get groupByDate(): boolean {
        return this._getOption('groupByDate');
    }
    set groupByDate(value: boolean) {
        this._setOption('groupByDate', value);
    }


    /**
     * Specifies the resource kinds by which the scheduler's appointments are grouped in a timetable.
    
     */
    @Input()
    get groups(): Array<string> {
        return this._getOption('groups');
    }
    set groups(value: Array<string>) {
        this._setOption('groups', value);
    }


    /**
     * Specifies the UI component's height.
    
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies the time interval between when the date-time indicator changes its position, in milliseconds.
    
     */
    @Input()
    get indicatorUpdateInterval(): number {
        return this._getOption('indicatorUpdateInterval');
    }
    set indicatorUpdateInterval(value: number) {
        this._setOption('indicatorUpdateInterval', value);
    }


    /**
     * The latest date the UI component allows you to select.
    
     */
    @Input()
    get max(): Date | number | string {
        return this._getOption('max');
    }
    set max(value: Date | number | string) {
        this._setOption('max', value);
    }


    /**
     * Specifies the limit of full-sized appointments displayed per cell. Applies to all views except 'agenda'.
    
     */
    @Input()
    get maxAppointmentsPerCell(): number | string {
        return this._getOption('maxAppointmentsPerCell');
    }
    set maxAppointmentsPerCell(value: number | string) {
        this._setOption('maxAppointmentsPerCell', value);
    }


    /**
     * The earliest date the UI component allows you to select.
    
     */
    @Input()
    get min(): Date | number | string {
        return this._getOption('min');
    }
    set min(value: Date | number | string) {
        this._setOption('min', value);
    }


    /**
     * Specifies the text or HTML markup displayed by the UI component if the item collection is empty. Available for the Agenda view only.
    
     */
    @Input()
    get noDataText(): string {
        return this._getOption('noDataText');
    }
    set noDataText(value: string) {
        this._setOption('noDataText', value);
    }


    /**
     * Specifies the edit mode for recurring appointments.
    
     */
    @Input()
    get recurrenceEditMode(): string {
        return this._getOption('recurrenceEditMode');
    }
    set recurrenceEditMode(value: string) {
        this._setOption('recurrenceEditMode', value);
    }


    /**
     * Specifies the name of the data source item field that defines exceptions for the current recurring appointment.
    
     */
    @Input()
    get recurrenceExceptionExpr(): string {
        return this._getOption('recurrenceExceptionExpr');
    }
    set recurrenceExceptionExpr(value: string) {
        this._setOption('recurrenceExceptionExpr', value);
    }


    /**
     * Specifies the name of the data source item field that defines a recurrence rule for generating recurring appointments.
    
     */
    @Input()
    get recurrenceRuleExpr(): string {
        return this._getOption('recurrenceRuleExpr');
    }
    set recurrenceRuleExpr(value: string) {
        this._setOption('recurrenceRuleExpr', value);
    }


    /**
     * Specifies whether filtering is performed on the server or client side.
    
     */
    @Input()
    get remoteFiltering(): boolean {
        return this._getOption('remoteFiltering');
    }
    set remoteFiltering(value: boolean) {
        this._setOption('remoteFiltering', value);
    }


    /**
     * Specifies a custom template for resource headers.
    
     */
    @Input()
    get resourceCellTemplate(): any {
        return this._getOption('resourceCellTemplate');
    }
    set resourceCellTemplate(value: any) {
        this._setOption('resourceCellTemplate', value);
    }


    /**
     * Specifies an array of resources available in the scheduler.
    
     */
    @Input()
    get resources(): Array<any | { allowMultiple?: boolean, colorExpr?: string, dataSource?: DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any>, displayExpr?: Function | string, fieldExpr?: string, label?: string, useColorAsDefault?: boolean, valueExpr?: Function | string }> {
        return this._getOption('resources');
    }
    set resources(value: Array<any | { allowMultiple?: boolean, colorExpr?: string, dataSource?: DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any>, displayExpr?: Function | string, fieldExpr?: string, label?: string, useColorAsDefault?: boolean, valueExpr?: Function | string }>) {
        this._setOption('resources', value);
    }


    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Configures scrolling.
    
     */
    @Input()
    get scrolling(): dxSchedulerScrolling {
        return this._getOption('scrolling');
    }
    set scrolling(value: dxSchedulerScrolling) {
        this._setOption('scrolling', value);
    }


    /**
     * Currently selected cells' data.
    
     */
    @Input()
    get selectedCellData(): Array<any> {
        return this._getOption('selectedCellData');
    }
    set selectedCellData(value: Array<any>) {
        this._setOption('selectedCellData', value);
    }


    /**
     * Specifies whether to apply shading to cover the timetable up to the current time.
    
     */
    @Input()
    get shadeUntilCurrentTime(): boolean {
        return this._getOption('shadeUntilCurrentTime');
    }
    set shadeUntilCurrentTime(value: boolean) {
        this._setOption('shadeUntilCurrentTime', value);
    }


    /**
     * Specifies the 'All-day' panel's visibility. Setting this property to false hides the panel along with the all-day appointments.
    
     */
    @Input()
    get showAllDayPanel(): boolean {
        return this._getOption('showAllDayPanel');
    }
    set showAllDayPanel(value: boolean) {
        this._setOption('showAllDayPanel', value);
    }


    /**
     * Specifies the current date-time indicator's visibility.
    
     */
    @Input()
    get showCurrentTimeIndicator(): boolean {
        return this._getOption('showCurrentTimeIndicator');
    }
    set showCurrentTimeIndicator(value: boolean) {
        this._setOption('showCurrentTimeIndicator', value);
    }


    /**
     * Specifies the name of the data source item field that defines the start of an appointment.
    
     */
    @Input()
    get startDateExpr(): string {
        return this._getOption('startDateExpr');
    }
    set startDateExpr(value: string) {
        this._setOption('startDateExpr', value);
    }


    /**
     * Specifies the name of the data source item field that defines the timezone of the appointment start date.
    
     */
    @Input()
    get startDateTimeZoneExpr(): string {
        return this._getOption('startDateTimeZoneExpr');
    }
    set startDateTimeZoneExpr(value: string) {
        this._setOption('startDateTimeZoneExpr', value);
    }


    /**
     * Specifies the first hour on the time scale. Accepts integer values from 0 to 24.
    
     */
    @Input()
    get startDayHour(): number {
        return this._getOption('startDayHour');
    }
    set startDayHour(value: number) {
        this._setOption('startDayHour', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies the name of the data source item field that holds the subject of an appointment.
    
     */
    @Input()
    get textExpr(): string {
        return this._getOption('textExpr');
    }
    set textExpr(value: string) {
        this._setOption('textExpr', value);
    }


    /**
     * Specifies a custom template for time scale items.
    
     */
    @Input()
    get timeCellTemplate(): any {
        return this._getOption('timeCellTemplate');
    }
    set timeCellTemplate(value: any) {
        this._setOption('timeCellTemplate', value);
    }


    /**
     * Specifies the time zone for the Scheduler's grid. Accepts values from the IANA time zone database.
    
     */
    @Input()
    get timeZone(): string {
        return this._getOption('timeZone');
    }
    set timeZone(value: string) {
        this._setOption('timeZone', value);
    }


    /**
     * Specifies whether a user can switch views using tabs or a drop-down menu.
    
     */
    @Input()
    get useDropDownViewSwitcher(): boolean {
        return this._getOption('useDropDownViewSwitcher');
    }
    set useDropDownViewSwitcher(value: boolean) {
        this._setOption('useDropDownViewSwitcher', value);
    }


    /**
     * Specifies and configures the views to be available in the view switcher.
    
     */
    @Input()
    get views(): string | Array<string | any | { agendaDuration?: number, appointmentCollectorTemplate?: any, appointmentTemplate?: any, appointmentTooltipTemplate?: any, cellDuration?: number, dataCellTemplate?: any, dateCellTemplate?: any, dropDownAppointmentTemplate?: any, endDayHour?: number, firstDayOfWeek?: number | string, groupByDate?: boolean, groupOrientation?: string, groups?: Array<string>, intervalCount?: number, maxAppointmentsPerCell?: number | string, name?: string, resourceCellTemplate?: any, scrolling?: DevExpress.ui.dxSchedulerScrolling, startDate?: Date | number | string, startDayHour?: number, timeCellTemplate?: any, type?: string }> {
        return this._getOption('views');
    }
    set views(value: string | Array<string | any | { agendaDuration?: number, appointmentCollectorTemplate?: any, appointmentTemplate?: any, appointmentTooltipTemplate?: any, cellDuration?: number, dataCellTemplate?: any, dateCellTemplate?: any, dropDownAppointmentTemplate?: any, endDayHour?: number, firstDayOfWeek?: number | string, groupByDate?: boolean, groupOrientation?: string, groups?: Array<string>, intervalCount?: number, maxAppointmentsPerCell?: number | string, name?: string, resourceCellTemplate?: any, scrolling?: DevExpress.ui.dxSchedulerScrolling, startDate?: Date | number | string, startDayHour?: number, timeCellTemplate?: any, type?: string }>) {
        this._setOption('views', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component's width.
    
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }

    /**
    
     * A function that is executed after an appointment is added to the data source.
    
    
     */
    @Output() onAppointmentAdded: EventEmitter<any>;

    /**
    
     * A function that is executed before an appointment is added to the data source.
    
    
     */
    @Output() onAppointmentAdding: EventEmitter<any>;

    /**
    
     * A function that is executed when an appointment is clicked or tapped.
    
    
     */
    @Output() onAppointmentClick: EventEmitter<any>;

    /**
    
     * A function that is executed when a user attempts to open the browser's context menu for an appointment. Allows you to replace this context menu with a custom context menu.
    
    
     */
    @Output() onAppointmentContextMenu: EventEmitter<any>;

    /**
    
     * A function that is executed when an appointment is double-clicked or double-tapped.
    
    
     */
    @Output() onAppointmentDblClick: EventEmitter<any>;

    /**
    
     * A function that is executed after an appointment is deleted from the data source.
    
    
     */
    @Output() onAppointmentDeleted: EventEmitter<any>;

    /**
    
     * A function that is executed before an appointment is deleted from the data source.
    
    
     */
    @Output() onAppointmentDeleting: EventEmitter<any>;

    /**
    
     * A function that is executed before an appointment details form is opened. Use this function to customize the form.
    
    
     */
    @Output() onAppointmentFormOpening: EventEmitter<any>;

    /**
    
     * A function that is executed when an appointment is rendered.
    
    
     */
    @Output() onAppointmentRendered: EventEmitter<any>;

    /**
    
     * A function that is executed after an appointment is updated in the data source.
    
    
     */
    @Output() onAppointmentUpdated: EventEmitter<any>;

    /**
    
     * A function that is executed before an appointment is updated in the data source.
    
    
     */
    @Output() onAppointmentUpdating: EventEmitter<any>;

    /**
    
     * A function that is executed when a view cell is clicked.
    
    
     */
    @Output() onCellClick: EventEmitter<any>;

    /**
    
     * A function that is executed when a user attempts to open the browser's context menu for a cell. Allows you to replace this context menu with a custom context menu.
    
    
     */
    @Output() onCellContextMenu: EventEmitter<any>;

    /**
    
     * A function that is executed when the UI component's content is ready and each time the content is changed.
    
    
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() adaptivityEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() allDayExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() appointmentCollectorTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() appointmentDraggingChange: EventEmitter<{ autoScroll?: boolean, data?: any, group?: string, onAdd?: Function, onDragEnd?: Function, onDragMove?: Function, onDragStart?: Function, onRemove?: Function, scrollSensitivity?: number, scrollSpeed?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() appointmentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() appointmentTooltipTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() cellDurationChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() crossScrollingEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentDateChange: EventEmitter<Date | number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentViewChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customizeDateNavigatorTextChange: EventEmitter<Function>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dataCellTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dataSourceChange: EventEmitter<DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<DevExpress.ui.dxSchedulerAppointment>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dateCellTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dateSerializationFormatChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() descriptionExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() dropDownAppointmentTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() editingChange: EventEmitter<boolean | { allowAdding?: boolean, allowDeleting?: boolean, allowDragging?: boolean, allowEditingTimeZones?: boolean, allowResizing?: boolean, allowTimeZoneEditing?: boolean, allowUpdating?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() endDateExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() endDateTimeZoneExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() endDayHourChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() firstDayOfWeekChange: EventEmitter<number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() groupByDateChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() groupsChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hintChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() indicatorUpdateIntervalChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() maxChange: EventEmitter<Date | number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() maxAppointmentsPerCellChange: EventEmitter<number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() minChange: EventEmitter<Date | number | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() noDataTextChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() recurrenceEditModeChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() recurrenceExceptionExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() recurrenceRuleExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() remoteFilteringChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() resourceCellTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() resourcesChange: EventEmitter<Array<any | { allowMultiple?: boolean, colorExpr?: string, dataSource?: DevExpress.data.DataSource | DevExpress.data.DataSourceOptions | string | Array<any>, displayExpr?: Function | string, fieldExpr?: string, label?: string, useColorAsDefault?: boolean, valueExpr?: Function | string }>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() scrollingChange: EventEmitter<dxSchedulerScrolling>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedCellDataChange: EventEmitter<Array<any>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() shadeUntilCurrentTimeChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showAllDayPanelChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showCurrentTimeIndicatorChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() startDateExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() startDateTimeZoneExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() startDayHourChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() textExprChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() timeCellTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() timeZoneChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() useDropDownViewSwitcherChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() viewsChange: EventEmitter<string | Array<string | any | { agendaDuration?: number, appointmentCollectorTemplate?: any, appointmentTemplate?: any, appointmentTooltipTemplate?: any, cellDuration?: number, dataCellTemplate?: any, dateCellTemplate?: any, dropDownAppointmentTemplate?: any, endDayHour?: number, firstDayOfWeek?: number | string, groupByDate?: boolean, groupOrientation?: string, groups?: Array<string>, intervalCount?: number, maxAppointmentsPerCell?: number | string, name?: string, resourceCellTemplate?: any, scrolling?: DevExpress.ui.dxSchedulerScrolling, startDate?: Date | number | string, startDayHour?: number, timeCellTemplate?: any, type?: string }>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<number | Function | string>;




    @ContentChildren(DxiResourceComponent)
    get resourcesChildren(): QueryList<DxiResourceComponent> {
        return this._getOption('resources');
    }
    set resourcesChildren(value) {
        this.setChildren('resources', value);
    }

    @ContentChildren(DxiViewComponent)
    get viewsChildren(): QueryList<DxiViewComponent> {
        return this._getOption('views');
    }
    set viewsChildren(value) {
        this.setChildren('views', value);
    }




    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'appointmentAdded', emit: 'onAppointmentAdded' },
            { subscribe: 'appointmentAdding', emit: 'onAppointmentAdding' },
            { subscribe: 'appointmentClick', emit: 'onAppointmentClick' },
            { subscribe: 'appointmentContextMenu', emit: 'onAppointmentContextMenu' },
            { subscribe: 'appointmentDblClick', emit: 'onAppointmentDblClick' },
            { subscribe: 'appointmentDeleted', emit: 'onAppointmentDeleted' },
            { subscribe: 'appointmentDeleting', emit: 'onAppointmentDeleting' },
            { subscribe: 'appointmentFormOpening', emit: 'onAppointmentFormOpening' },
            { subscribe: 'appointmentRendered', emit: 'onAppointmentRendered' },
            { subscribe: 'appointmentUpdated', emit: 'onAppointmentUpdated' },
            { subscribe: 'appointmentUpdating', emit: 'onAppointmentUpdating' },
            { subscribe: 'cellClick', emit: 'onCellClick' },
            { subscribe: 'cellContextMenu', emit: 'onCellContextMenu' },
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'adaptivityEnabledChange' },
            { emit: 'allDayExprChange' },
            { emit: 'appointmentCollectorTemplateChange' },
            { emit: 'appointmentDraggingChange' },
            { emit: 'appointmentTemplateChange' },
            { emit: 'appointmentTooltipTemplateChange' },
            { emit: 'cellDurationChange' },
            { emit: 'crossScrollingEnabledChange' },
            { emit: 'currentDateChange' },
            { emit: 'currentViewChange' },
            { emit: 'customizeDateNavigatorTextChange' },
            { emit: 'dataCellTemplateChange' },
            { emit: 'dataSourceChange' },
            { emit: 'dateCellTemplateChange' },
            { emit: 'dateSerializationFormatChange' },
            { emit: 'descriptionExprChange' },
            { emit: 'disabledChange' },
            { emit: 'dropDownAppointmentTemplateChange' },
            { emit: 'editingChange' },
            { emit: 'elementAttrChange' },
            { emit: 'endDateExprChange' },
            { emit: 'endDateTimeZoneExprChange' },
            { emit: 'endDayHourChange' },
            { emit: 'firstDayOfWeekChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'groupByDateChange' },
            { emit: 'groupsChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'indicatorUpdateIntervalChange' },
            { emit: 'maxChange' },
            { emit: 'maxAppointmentsPerCellChange' },
            { emit: 'minChange' },
            { emit: 'noDataTextChange' },
            { emit: 'recurrenceEditModeChange' },
            { emit: 'recurrenceExceptionExprChange' },
            { emit: 'recurrenceRuleExprChange' },
            { emit: 'remoteFilteringChange' },
            { emit: 'resourceCellTemplateChange' },
            { emit: 'resourcesChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'scrollingChange' },
            { emit: 'selectedCellDataChange' },
            { emit: 'shadeUntilCurrentTimeChange' },
            { emit: 'showAllDayPanelChange' },
            { emit: 'showCurrentTimeIndicatorChange' },
            { emit: 'startDateExprChange' },
            { emit: 'startDateTimeZoneExprChange' },
            { emit: 'startDayHourChange' },
            { emit: 'tabIndexChange' },
            { emit: 'textExprChange' },
            { emit: 'timeCellTemplateChange' },
            { emit: 'timeZoneChange' },
            { emit: 'useDropDownViewSwitcherChange' },
            { emit: 'viewsChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxScheduler(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('dataSource', changes);
        this.setupChanges('groups', changes);
        this.setupChanges('resources', changes);
        this.setupChanges('selectedCellData', changes);
        this.setupChanges('views', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('dataSource');
        this._idh.doCheck('groups');
        this._idh.doCheck('resources');
        this._idh.doCheck('selectedCellData');
        this._idh.doCheck('views');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxoAppointmentDraggingModule,
    DxoEditingModule,
    DxiResourceModule,
    DxoScrollingModule,
    DxiViewModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxSchedulerComponent
  ],
  exports: [
    DxSchedulerComponent,
    DxoAppointmentDraggingModule,
    DxoEditingModule,
    DxiResourceModule,
    DxoScrollingModule,
    DxiViewModule,
    DxTemplateModule
  ]
})
export class DxSchedulerModule { }
